<template>
  <v-app style="background-color: #f8f9fb">
    <v-container>
      <v-col cols="12">
        <v-card elevation="0" class="px-5 py-3">
          <v-card-title class="px-0 pt-0">Upload de imagem — Campanha</v-card-title>
          <v-row align="end">
            <v-col cols="12" sm="6" md="4">
              <span>Nome da campanha</span>
              <v-text-field
                v-model="nomeCampanha"
                label="Ex: Black Friday 2026"
                dense
                solo-inverted
                :disabled="enviando"
                maxlength="255"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <span>Imagem (máx. 5 MB)</span>
              <v-file-input
                v-model="arquivo"
                accept="image/*"
                label="Selecione a imagem"
                dense
                solo-inverted
                prepend-icon="mdi-image"
                show-size
                :disabled="enviando"
                :rules="fileRules"
              ></v-file-input>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <span>&nbsp;</span>
              <v-btn
                color="#4682B4"
                block
                height="40"
                style="font-size: 14px; font-weight: bold"
                :loading="enviando"
                :disabled="!podeEnviar"
                @click="enviar"
              >
                Enviar
                <v-icon right dark>mdi-cloud-upload</v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <v-alert v-if="mensagem" :type="tipoMensagem" dense class="mt-2 mb-0">
            {{ mensagem }}
          </v-alert>
        </v-card>
      </v-col>
    </v-container>

    <v-container style="background-color: #eee" fluid tag="section">
      <v-card elevation="0" class="pa-4">
        <div class="d-flex align-center justify-space-between mb-3">
          <v-card-title class="px-0 py-0">Últimas 20 imagens</v-card-title>
          <v-btn small text color="primary" :loading="carregandoLista" @click="carregarLista">
            Atualizar
          </v-btn>
        </div>

        <v-data-table
          :headers="headers"
          :items="imagens"
          :loading="carregandoLista"
          hide-default-footer
          disable-pagination
          class="elevation-0"
        >
          <template v-slot:item.preview="{ item }">
            <a :href="item.urlPublica" target="_blank" rel="noopener noreferrer">
              <img
                :src="item.urlPublica"
                alt="preview"
                style="width: 64px; height: 64px; object-fit: cover; border-radius: 4px"
              />
            </a>
          </template>
          <template v-slot:item.urlPublica="{ item }">
            <div class="d-flex align-center" style="gap: 8px; max-width: 360px">
              <a
                :href="item.urlPublica"
                target="_blank"
                rel="noopener noreferrer"
                class="text-truncate"
                style="max-width: 280px"
              >
                {{ item.urlPublica }}
              </a>
              <v-btn icon x-small title="Copiar link" @click="copiarLink(item.urlPublica)">
                <v-icon small>mdi-content-copy</v-icon>
              </v-btn>
            </div>
          </template>
          <template v-slot:item.tamanhoBytes="{ item }">
            {{ formatarTamanho(item.tamanhoBytes) }}
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <v-snackbar v-model="snackbar" :timeout="2500" color="success">
      Link copiado
    </v-snackbar>
  </v-app>
</template>

<script>
import Campanha from "@/services/campanhaUpload";

const MAX_BYTES = 5 * 1024 * 1024;

export default {
  name: "CampanhaUpload",
  data: () => ({
    nomeCampanha: "",
    arquivo: null,
    enviando: false,
    carregandoLista: false,
    mensagem: "",
    tipoMensagem: "success",
    imagens: [],
    snackbar: false,
    headers: [
      { text: "Preview", value: "preview", sortable: false },
      { text: "Campanha", value: "nomeCampanha" },
      { text: "Link", value: "urlPublica", sortable: false },
      { text: "Usuário", value: "nomeUsuario" },
      { text: "Tamanho", value: "tamanhoBytes" },
      { text: "Data", value: "dataInsert" },
    ],
    fileRules: [
      (v) => !v || v.size <= MAX_BYTES || "Máximo 5 MB",
      (v) =>
        !v ||
        (v.type && v.type.startsWith("image/")) ||
        "Apenas arquivos de imagem",
    ],
  }),
  computed: {
    podeEnviar() {
      return (
        !!this.nomeCampanha.trim() &&
        !!this.arquivo &&
        this.arquivo.size <= MAX_BYTES &&
        this.arquivo.type &&
        this.arquivo.type.startsWith("image/")
      );
    },
  },
  mounted() {
    this.carregarLista();
  },
  methods: {
    async enviar() {
      if (!this.podeEnviar) return;
      this.enviando = true;
      this.mensagem = "";
      try {
        const { data } = await Campanha.upload(this.nomeCampanha.trim(), this.arquivo);
        this.tipoMensagem = "success";
        this.mensagem = `Upload concluído. Link: ${data.urlPublica}`;
        this.nomeCampanha = "";
        this.arquivo = null;
        await this.carregarLista();
      } catch (e) {
        this.tipoMensagem = "error";
        this.mensagem =
          e?.response?.data?.mensagem || "Erro ao enviar a imagem.";
      } finally {
        this.enviando = false;
      }
    },
    async carregarLista() {
      this.carregandoLista = true;
      try {
        const { data } = await Campanha.listarUltimas();
        this.imagens = data || [];
      } catch (e) {
        this.tipoMensagem = "error";
        this.mensagem =
          e?.response?.data?.mensagem || "Erro ao carregar a lista de imagens.";
      } finally {
        this.carregandoLista = false;
      }
    },
    formatarTamanho(bytes) {
      if (!bytes && bytes !== 0) return "-";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    },
    async copiarLink(url) {
      try {
        await navigator.clipboard.writeText(url);
        this.snackbar = true;
      } catch (e) {
        window.prompt("Copie o link:", url);
      }
    },
  },
};
</script>
